import React, { useEffect, useState } from 'react';
import { EditorView } from '@components/BlockNote/EditorView';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTrackEvent } from '@services/statsig/hooks/useTrackEvent';
import type { LearningRuleDocument4 } from '@store/generated/rules';
import { useAppDispatch } from '@store/hooks';

import { useCreateAdminRuleMutation, useUpdateAdminRuleMutation } from '../store/adminApi';
import { useCreateRuleMutation, useUpdateRuleMutation } from '../store/api';
import type {
  ComplianceRuleDocument,
  LearningRuleDocument,
  ProviderRuleCreateRequest,
  ProviderRuleResponse,
  ProviderRuleUpdateRequest,
} from '../types';

interface RuleFormPanelProps {
  mode: 'create' | 'edit';
  rule?: ProviderRuleResponse | null;
  onClose: () => void;
  onSuccess?: () => void;
  /** Whether this is being used in admin context */
  isAdmin?: boolean;
  /** Organization ID for admin context (null = global rules) */
  organizationId?: string | null;
}

export const RuleFormPanel: React.FC<RuleFormPanelProps> = ({
  mode,
  rule,
  onClose,
  onSuccess,
  isAdmin = false,
  organizationId,
}) => {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();

  // Provider mutations
  const [createProviderRule, { isLoading: isCreatingProvider }] = useCreateRuleMutation();
  const [updateProviderRule, { isLoading: isUpdatingProvider }] = useUpdateRuleMutation();

  // Admin mutations
  const [createAdminRule, { isLoading: isCreatingAdmin }] = useCreateAdminRuleMutation();
  const [updateAdminRule, { isLoading: isUpdatingAdmin }] = useUpdateAdminRuleMutation();

  // Use admin or provider mutations based on context
  const isCreating = isAdmin ? isCreatingAdmin : isCreatingProvider;
  const isUpdating = isAdmin ? isUpdatingAdmin : isUpdatingProvider;
  const isLoading = mode === 'create' ? isCreating : isUpdating;

  // Determine rule type - for edit mode, use existing rule type; for create, default to compliance
  const [ruleType, setRuleType] = useState<'compliance' | 'learning'>(
    rule?.rule_type || 'compliance',
  );

  // Compliance rule form state - updated to ComplianceRuleV2 schema
  const [complianceData, setComplianceData] = useState<Partial<ComplianceRuleDocument>>({
    title: '',
    body_md: '',
    selector_text: '',
    always_select: false,
    state: 'active',
    facets: {
      cpt: [],
      payer: [],
      provider_type: [],
    },
    source: {
      document_title: '',
      document_date: '',
      source_uri: '',
    },
  });

  // Learning rule form state
  const [learningData, setLearningData] = useState<Partial<LearningRuleDocument>>({
    title: '',
    teaching_summary: '',
    relevant_context: '',
    when_to_follow: '',
    recommended_actions: '',
    clinical_rationale: '',
    source_document: '',
    source_date: '',
    source_citation: '',
    source_link: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when rule changes or panel opens
  useEffect(() => {
    if (mode === 'edit' && rule) {
      setRuleType(rule.rule_type);
      if (rule.rule_type === 'compliance') {
        const compRule = rule.rule as ComplianceRuleDocument;
        setComplianceData({
          title: compRule.title || '',
          body_md: compRule.body_md || '',
          selector_text: compRule.selector_text || '',
          always_select: compRule.always_select || false,
          state: compRule.state || 'active',
          facets: {
            cpt: compRule.facets?.cpt || [],
            payer: compRule.facets?.payer || [],
            provider_type: compRule.facets?.provider_type || [],
          },
          source: {
            document_title: compRule.source?.document_title || '',
            document_date: compRule.source?.document_date || '',
            source_uri: compRule.source?.source_uri || '',
          },
        });
      } else {
        const learnRule = rule.rule as LearningRuleDocument;
        setLearningData({
          title: learnRule.title || '',
          teaching_summary: learnRule.teaching_summary || '',
          relevant_context: learnRule.relevant_context || '',
          when_to_follow: learnRule.when_to_follow || '',
          recommended_actions: learnRule.recommended_actions || '',
          clinical_rationale: learnRule.clinical_rationale || '',
          source_document: learnRule.source_document || '',
          source_date: learnRule.source_date || '',
          source_citation: learnRule.source_citation || '',
          source_link: learnRule.source_link || '',
        });
      }
    } else {
      // Reset for create mode
      setComplianceData({
        title: '',
        body_md: '',
        selector_text: '',
        always_select: false,
        state: 'active',
        facets: {
          cpt: [],
          payer: [],
          provider_type: [],
        },
        source: {
          document_title: '',
          document_date: '',
          source_uri: '',
        },
      });
      setLearningData({
        title: '',
        teaching_summary: '',
        relevant_context: '',
        when_to_follow: '',
        recommended_actions: '',
        clinical_rationale: '',
        source_document: '',
        source_date: '',
        source_citation: '',
        source_link: '',
      });
    }
    setErrors({});
  }, [mode, rule]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (ruleType === 'compliance') {
      if (!complianceData.body_md?.trim()) {
        newErrors.body_md = 'Rule body (markdown) is required';
      }
      if (!complianceData.selector_text?.trim()) {
        newErrors.selector_text = 'Selector text is required';
      }
      if (complianceData.selector_text && complianceData.selector_text.length > 150) {
        newErrors.selector_text = 'Selector text should be 100-150 characters';
      }
    } else {
      if (!learningData.title?.trim()) {
        newErrors.title = 'Title is required';
      }
      if (!learningData.teaching_summary?.trim()) {
        newErrors.teaching_summary = 'Teaching summary is required';
      }
      if (!learningData.when_to_follow?.trim()) {
        newErrors.when_to_follow = 'When to follow is required';
      }
      if (!learningData.recommended_actions?.trim()) {
        newErrors.recommended_actions = 'Recommended actions is required';
      }
      if (!learningData.clinical_rationale?.trim()) {
        newErrors.clinical_rationale = 'Clinical rationale is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        // Determine organization_id based on context
        const targetOrgId = isAdmin
          ? (organizationId ?? 'global') // Admin can create global or org-specific rules
          : ''; // Provider rules - org_id set by backend

        if (isAdmin) {
          // Admin create - uses admin API with explicit organization_id
          const adminRequest = {
            rule: {
              rule_id: '', // Will be generated by backend
              body_md: complianceData.body_md || '',
              selector_text: complianceData.selector_text || '',
              title: complianceData.title || null,
              always_select: complianceData.always_select || false,
              state: complianceData.state || 'active',
              facets: complianceData.facets || {
                cpt: [],
                payer: [],
                provider_type: [],
              },
              source: complianceData.source || {
                document_title: null,
                document_date: null,
                source_uri: null,
              },
            } as ComplianceRuleDocument,
          };

          const result = await createAdminRule(adminRequest).unwrap();
          trackEvent('admin_rule_created', {
            metadata: {
              rule_id: result.rule_id,
              organization_id: targetOrgId,
              is_global: targetOrgId === 'global',
            },
          });
        } else {
          // Provider create
          const request: ProviderRuleCreateRequest = {
            rule_type: ruleType,
            rule:
              ruleType === 'compliance'
                ? ({
                    rule_id: '', // Will be generated by backend
                    body_md: complianceData.body_md || '',
                    selector_text: complianceData.selector_text || '',
                    title: complianceData.title || null,
                    always_select: complianceData.always_select || false,
                    state: complianceData.state || 'active',
                    facets: complianceData.facets || {
                      cpt: [],
                      payer: [],
                      provider_type: [],
                    },
                    source: complianceData.source || {
                      document_title: null,
                      document_date: null,
                      source_uri: null,
                    },
                  } as ComplianceRuleDocument)
                : ({
                    ...learningData,
                    rule_id: '', // Will be generated by backend
                    organization_id: '', // Will be set by backend
                    type: 'learning',
                    clinical_rationale: learningData.clinical_rationale ?? '',
                  } as unknown as LearningRuleDocument4),
          };

          const result = await createProviderRule(request).unwrap();
          trackEvent('rule_created', {
            metadata: {
              rule_id: result.rule_id,
              rule_type: result.rule_type,
            },
          });
        }
      } else {
        if (!rule) return;

        if (isAdmin) {
          // Admin update
          const adminRequest = {
            rule: {
              rule_id: rule.rule_id,
              body_md: complianceData.body_md || '',
              selector_text: complianceData.selector_text || '',
              title: complianceData.title || null,
              always_select: complianceData.always_select || false,
              state: complianceData.state || 'active',
              facets: complianceData.facets || {
                cpt: [],
                payer: [],
                provider_type: [],
              },
              source: complianceData.source || {
                document_title: null,
                document_date: null,
                source_uri: null,
              },
            } as ComplianceRuleDocument,
          };

          const result = await updateAdminRule({
            ruleId: rule.rule_id,
            body: adminRequest,
          }).unwrap();

          trackEvent('admin_rule_updated', {
            metadata: {
              rule_id: result.rule_id,
            },
          });
        } else {
          // Provider update
          const request: ProviderRuleUpdateRequest = {
            rule_type: ruleType,
            rule:
              ruleType === 'compliance'
                ? ({
                    rule_id: rule.rule_id,
                    body_md: complianceData.body_md || '',
                    selector_text: complianceData.selector_text || '',
                    title: complianceData.title || null,
                    always_select: complianceData.always_select || false,
                    state: complianceData.state || 'active',
                    facets: complianceData.facets || {
                      cpt: [],
                      payer: [],
                      provider_type: [],
                    },
                    source: complianceData.source || {
                      document_title: null,
                      document_date: null,
                      source_uri: null,
                    },
                  } as ComplianceRuleDocument)
                : ({
                    ...learningData,
                    rule_id: rule.rule_id,
                    organization_id: rule.organization_id || '',
                    type: 'learning',
                    clinical_rationale: learningData.clinical_rationale ?? '',
                  } as LearningRuleDocument4),
          };

          const result = await updateProviderRule({ ruleId: rule.rule_id, body: request }).unwrap();

          // The mutation already invalidates the Rule tag, which will cause useGetRuleQuery to refetch
          // No need to manually invalidate here - the mutation's invalidatesTags handles it

          trackEvent('rule_updated', {
            metadata: {
              rule_id: result.rule_id,
              rule_type: result.rule_type,
            },
          });
        }
      }

      // For edit mode, only call onSuccess to switch back to view mode
      // Don't call onClose() as that would clear selectedRuleId and close the panel
      // The rule detail will auto-refresh via query invalidation
      if (mode === 'edit') {
        onSuccess?.();
      } else {
        // For create mode, close the panel
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to save rule:', error);
      setErrors({ submit: 'Failed to save rule. Please try again.' });
    }
  };

  const renderComplianceFields = () => {
    const handleBodyMdChange = (markdown: string) => {
      setComplianceData({ ...complianceData, body_md: markdown });
    };

    const handleCptCodesChange = (value: string) => {
      const codes = value
        .split(',')
        .map(c => c.trim())
        .filter(c => c.length > 0);
      setComplianceData({
        ...complianceData,
        facets: {
          ...complianceData.facets,
          cpt: codes,
        },
      });
    };

    const handlePayersChange = (value: string) => {
      const payers = value
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);
      setComplianceData({
        ...complianceData,
        facets: {
          ...complianceData.facets,
          payer: payers,
        },
      });
    };

    const handleProviderTypesChange = (value: string) => {
      const types = value
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      setComplianceData({
        ...complianceData,
        facets: {
          ...complianceData.facets,
          provider_type: types,
        },
      });
    };

    return (
    <Stack spacing={2}>
      <TextField
        label="Title (Optional)"
        value={complianceData.title || ''}
        onChange={e => setComplianceData({ ...complianceData, title: e.target.value })}
        fullWidth
        size="small"
      />

      <TextField
          label="Selector Text *"
          value={complianceData.selector_text || ''}
          onChange={e => setComplianceData({ ...complianceData, selector_text: e.target.value })}
        fullWidth
        multiline
          rows={2}
        required
          error={!!errors.selector_text}
          helperText={errors.selector_text || 'Compact one-liner for LLM rule selection (~100-150 chars)'}
        size="small"
          placeholder="e.g., CPT 99212-99215 — PDMP review documented when prescribing controlled substances"
        />

        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Rule Body (Markdown) *
          </Typography>
          <Box
            sx={{
              border: `1px solid ${errors.body_md ? '#d32f2f' : '#e0e0e0'}`,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <EditorView
              markdown={complianceData.body_md || ''}
              editable={true}
              onMarkdownChange={handleBodyMdChange}
              sideMenu={true}
              formattingToolbar={true}
              styleId={`compliance-rule-form-${rule?.rule_id || 'new'}`}
            />
          </Box>
          {errors.body_md && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {errors.body_md}
            </Typography>
          )}
        </Box>

      <FormControl fullWidth size="small">
          <InputLabel>State</InputLabel>
        <Select
            value={complianceData.state || 'active'}
            label="State"
            onChange={e =>
              setComplianceData({
                ...complianceData,
                state: e.target.value as 'active' | 'deprecated',
              })
            }
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="deprecated">Deprecated</MenuItem>
        </Select>
      </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={complianceData.always_select || false}
        onChange={e =>
                setComplianceData({ ...complianceData, always_select: e.target.checked })
              }
            />
          }
          label="Always Select (bypass LLM selection)"
        />

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Facets (Applicability Filters)
        </Typography>

        <TextField
          label="CPT Codes (Optional, comma-separated)"
          value={(complianceData.facets?.cpt || []).join(', ')}
          onChange={e => handleCptCodesChange(e.target.value)}
        fullWidth
        size="small"
          placeholder="99213, 99214, 99215"
      />

      <TextField
          label="Payers (Optional, comma-separated)"
          value={(complianceData.facets?.payer || []).join(', ')}
          onChange={e => handlePayersChange(e.target.value)}
        fullWidth
        size="small"
          placeholder="Medicare, Optum, Cigna"
      />

      <TextField
          label="Provider Types (Optional, comma-separated)"
          value={(complianceData.facets?.provider_type || []).join(', ')}
          onChange={e => handleProviderTypesChange(e.target.value)}
          fullWidth
          size="small"
          placeholder="Behavioral Health, Psychiatry, PMHNP"
        />

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Source Information
        </Typography>

        <TextField
          label="Source Document Title (Optional)"
          value={complianceData.source?.document_title || ''}
          onChange={e =>
            setComplianceData({
              ...complianceData,
              source: {
                ...complianceData.source,
                document_title: e.target.value || null,
              },
            })
          }
        fullWidth
        size="small"
      />

      <TextField
          label="Source Document Date (Optional)"
          value={complianceData.source?.document_date || ''}
          onChange={e =>
            setComplianceData({
              ...complianceData,
              source: {
                ...complianceData.source,
                document_date: e.target.value || null,
              },
            })
          }
        fullWidth
        size="small"
        placeholder="YYYY-MM-DD or YYYY"
      />

        <TextField
          label="Source URI (Optional)"
          value={complianceData.source?.source_uri || ''}
          onChange={e =>
            setComplianceData({
              ...complianceData,
              source: {
                ...complianceData.source,
                source_uri: e.target.value || null,
              },
            })
          }
          fullWidth
          size="small"
          placeholder="https://..."
        />
    </Stack>
  );
  };

  const renderLearningFields = () => (
    <Stack spacing={2}>
      <TextField
        label="Title *"
        value={learningData.title || ''}
        onChange={e => setLearningData({ ...learningData, title: e.target.value })}
        fullWidth
        required
        error={!!errors.title}
        helperText={errors.title}
        size="small"
      />

      <TextField
        label="Teaching Summary *"
        value={learningData.teaching_summary || ''}
        onChange={e => setLearningData({ ...learningData, teaching_summary: e.target.value })}
        fullWidth
        multiline
        rows={4}
        required
        error={!!errors.teaching_summary}
        helperText={errors.teaching_summary}
        size="small"
      />

      <TextField
        label="Relevant Context (Optional)"
        value={learningData.relevant_context || ''}
        onChange={e => setLearningData({ ...learningData, relevant_context: e.target.value })}
        fullWidth
        multiline
        rows={3}
        size="small"
      />

      <TextField
        label="When to Follow *"
        value={learningData.when_to_follow || ''}
        onChange={e => setLearningData({ ...learningData, when_to_follow: e.target.value })}
        fullWidth
        multiline
        rows={3}
        required
        error={!!errors.when_to_follow}
        helperText={errors.when_to_follow}
        size="small"
      />

      <TextField
        label="Recommended Actions *"
        value={learningData.recommended_actions || ''}
        onChange={e => setLearningData({ ...learningData, recommended_actions: e.target.value })}
        fullWidth
        multiline
        rows={3}
        required
        error={!!errors.recommended_actions}
        helperText={errors.recommended_actions}
        size="small"
      />

      <TextField
        label="Clinical Rationale *"
        value={learningData.clinical_rationale || ''}
        onChange={e => setLearningData({ ...learningData, clinical_rationale: e.target.value })}
        fullWidth
        multiline
        rows={3}
        required
        error={!!errors.clinical_rationale}
        helperText={errors.clinical_rationale}
        size="small"
      />

      <TextField
        label="Source Document (Optional)"
        value={learningData.source_document || ''}
        onChange={e => setLearningData({ ...learningData, source_document: e.target.value })}
        fullWidth
        size="small"
      />

      <TextField
        label="Source Citation (Optional)"
        value={learningData.source_citation || ''}
        onChange={e => setLearningData({ ...learningData, source_citation: e.target.value })}
        fullWidth
        size="small"
      />

      <TextField
        label="Source Link (Optional)"
        value={learningData.source_link || ''}
        onChange={e => setLearningData({ ...learningData, source_link: e.target.value })}
        fullWidth
        size="small"
        placeholder="https://..."
      />

      <TextField
        label="Source Date (Optional)"
        value={learningData.source_date || ''}
        onChange={e => setLearningData({ ...learningData, source_date: e.target.value })}
        fullWidth
        size="small"
        placeholder="YYYY-MM-DD or YYYY"
      />
    </Stack>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {mode === 'create' ? 'Create Rule' : 'Edit Rule'}
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: 'background.default' }}>
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Rule Type Selector (only in create mode) */}
          {mode === 'create' && (
            <FormControl fullWidth size="small">
              <InputLabel>Rule Type</InputLabel>
              <Select
                value={ruleType}
                label="Rule Type"
                onChange={e => setRuleType(e.target.value as 'compliance' | 'learning')}
                disabled={isLoading}
              >
                <MenuItem value="compliance">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Compliance" size="small" color="primary" />
                    <Typography variant="body2">Compliance Rule</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="learning">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Learning" size="small" color="secondary" />
                    <Typography variant="body2">Learning Rule</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Form Fields */}
          {ruleType === 'compliance' ? renderComplianceFields() : renderLearningFields()}
        </Stack>
      </Box>

      {/* Footer/Actions */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? mode === 'create'
                ? 'Creating...'
                : 'Updating...'
              : mode === 'create'
                ? 'Create Rule'
                : 'Update Rule'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};
